import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Box,
  MenuItem,
  Select,
  TextField,
  Typography,
  Button,
  FormControl,
  InputLabel,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { decoratedFetch } from "../../req_utils";
import { updateProjectFinancialInfo } from "../../reducers";
import InputAdornment from "@mui/material/InputAdornment";

const FinancialInfoPromptDialog = ({
  open,
  onClose,
  projectId,
  accountId,
  requiredFields,
  onComplete,
}) => {
  const dispatch = useDispatch();
  const project = useSelector((state) => state.project);
  const [proposals, setProposals] = useState([]);
  const [financialData, setFinancialData] = useState({
    invoicedProposalId: project?.invoiced_proposal_id || "",
    invoicedProposalPrice: project?.invoiced_proposal_price || "",
    jobCostingProposalId: project?.job_costing_proposal_id || "",
    jobCostingProposalPrice: project?.job_costing_proposal_price || "",
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Load proposals for the project
  useEffect(() => {
    if (open && projectId) {
      setLoading(true);
      decoratedFetch(`/list_proposals/${projectId}?financial_info=true`)
        .then((response) => {
          if (response.status !== 200) {
            throw new Error("Failed to fetch proposals");
          }
          return response.json();
        })
        .then((data) => {
          setProposals(data);
          setLoading(false);
        })
        .catch((error) => {
          console.error("Error loading proposals:", error);
          setLoading(false);
        });
    }
  }, [open, projectId]);

  // Sync local state with Redux project data
  useEffect(() => {
    if (project) {
      setFinancialData({
        invoicedProposalId: project.invoiced_proposal_id || "",
        invoicedProposalPrice: project.invoiced_proposal_price || "",
        jobCostingProposalId: project.job_costing_proposal_id || "",
        jobCostingProposalPrice: project.job_costing_proposal_price || "",
      });
    }
  }, [project]);

  const handleInvoicedProposalChange = (event) => {
    const selectedProposalId = event.target.value;
    const selectedProposal = proposals.find((p) => p.id === selectedProposalId);

    const newData = {
      invoiced_proposal_id: selectedProposalId,
      invoiced_proposal_price: selectedProposal?.proposal_total || "",
    };

    setFinancialData((prev) => ({
      ...prev,
      invoicedProposalId: selectedProposalId,
      invoicedProposalPrice: selectedProposal?.proposal_total || "",
    }));

    dispatch(updateProjectFinancialInfo(newData));
  };

  const handleJobCostingProposalChange = (event) => {
    const selectedProposalId = event.target.value;
    const selectedProposal = proposals.find((p) => p.id === selectedProposalId);

    const newData = {
      job_costing_proposal_id: selectedProposalId,
      job_costing_proposal_price: selectedProposal?.proposal_total || "",
    };

    setFinancialData((prev) => ({
      ...prev,
      jobCostingProposalId: selectedProposalId,
      jobCostingProposalPrice: selectedProposal?.proposal_total || "",
    }));

    dispatch(updateProjectFinancialInfo(newData));
  };

  const handleInvoicedPriceChange = (event) => {
    const price = event.target.value;

    setFinancialData((prev) => ({
      ...prev,
      invoicedProposalPrice: price,
    }));

    dispatch(updateProjectFinancialInfo({ invoiced_proposal_price: price }));
  };

  const handleJobCostingPriceChange = (event) => {
    const price = event.target.value;

    setFinancialData((prev) => ({
      ...prev,
      jobCostingProposalPrice: price,
    }));

    dispatch(updateProjectFinancialInfo({ job_costing_proposal_price: price }));
  };

  const validateRequiredFields = () => {
    const newErrors = {};

    if (
      requiredFields.includes("invoicedProposal") &&
      !financialData.invoicedProposalId
    ) {
      newErrors.invoicedProposal = "Invoiced proposal is required";
    }

    if (
      requiredFields.includes("invoicedProposal") &&
      !financialData.invoicedProposalPrice
    ) {
      newErrors.invoicedProposalPrice = "Invoiced proposal price is required";
    }

    if (
      requiredFields.includes("jobCosting") &&
      !financialData.jobCostingProposalId
    ) {
      newErrors.jobCosting = "Job costing proposal is required";
    }

    if (
      requiredFields.includes("jobCosting") &&
      !financialData.jobCostingProposalPrice
    ) {
      newErrors.jobCostingPrice = "Job costing proposal price is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleContinue = () => {
    if (validateRequiredFields()) {
      onComplete();
      onClose();
    }
  };

  const getDialogTitle = () => {
    if (
      requiredFields.includes("invoicedProposal") &&
      requiredFields.includes("jobCosting")
    ) {
      return "Financial Information Required";
    } else if (requiredFields.includes("invoicedProposal")) {
      return "Invoiced Proposal Required";
    } else if (requiredFields.includes("jobCosting")) {
      return "Job Costing Information Required";
    }
    return "Financial Information Required";
  };

  const getDialogMessage = () => {
    if (
      requiredFields.includes("invoicedProposal") &&
      requiredFields.includes("jobCosting")
    ) {
      return "Please provide both invoiced proposal and job costing information to continue.";
    } else if (requiredFields.includes("invoicedProposal")) {
      return "Please provide invoiced proposal information to continue.";
    } else if (requiredFields.includes("jobCosting")) {
      return "Please provide job costing information to continue.";
    }
    return "Please provide the required financial information to continue.";
  };

  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{ fontSize: "22px", paddingBottom: "0px", flexGrow: 1 }}
        >
          {getDialogTitle()}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ p: 2, textAlign: "center" }}>
            <Typography>Loading financial information...</Typography>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontSize: "22px", paddingBottom: "0px", flexGrow: 1 }}>
        {getDialogTitle()}
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={2} direction="row" marginTop={1}>
          <Grid item xs={12}>
            <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
              {getDialogMessage()}
            </Typography>
          </Grid>

          {/* Invoiced Proposal Section - Only show if required */}
          {requiredFields.includes("invoicedProposal") && (
            <Grid item xs={12} md={6}>
              <Typography
                variant="subtitle1"
                sx={{ mb: 2, color: "#002466", fontWeight: "bold" }}
              >
                Invoiced Proposal *
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl
                    fullWidth
                    size="small"
                    error={!!errors.invoicedProposal}
                  >
                    <InputLabel>Select Proposal</InputLabel>
                    <Select
                      value={financialData.invoicedProposalId}
                      onChange={handleInvoicedProposalChange}
                      label="Select Proposal"
                      MenuProps={{ disableScrollLock: true }}
                    >
                      <MenuItem value="">
                        <em>Select a proposal</em>
                      </MenuItem>
                      {proposals.map((proposal) => (
                        <MenuItem key={proposal.id} value={proposal.id}>
                          {proposal.name ||
                            proposal.custom_id ||
                            `Proposal ${proposal.id}`}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.invoicedProposal && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ mt: 0.5, display: "block" }}
                      >
                        {errors.invoicedProposal}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="Proposal Price"
                    value={financialData.invoicedProposalPrice}
                    onChange={handleInvoicedPriceChange}
                    fullWidth
                    size="small"
                    variant="outlined"
                    error={!!errors.invoicedProposalPrice}
                    helperText={errors.invoicedProposalPrice}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment
                          position="start"
                          sx={{ marginRight: "4px" }}
                        >
                          <span style={{ marginTop: "-3px", fontSize: "14px" }}>
                            $
                          </span>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
          )}

          {/* Job Costing Section - Only show if required */}
          {requiredFields.includes("jobCosting") && (
            <Grid item xs={12} md={6}>
              <Typography
                variant="subtitle1"
                sx={{ mb: 2, color: "#002466", fontWeight: "bold" }}
              >
                Job Costing *
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl
                    fullWidth
                    size="small"
                    error={!!errors.jobCosting}
                  >
                    <InputLabel>Select Proposal</InputLabel>
                    <Select
                      value={financialData.jobCostingProposalId}
                      onChange={handleJobCostingProposalChange}
                      label="Select Proposal"
                      MenuProps={{ disableScrollLock: true }}
                    >
                      <MenuItem value="">
                        <em>Select a proposal</em>
                      </MenuItem>
                      {proposals.map((proposal) => (
                        <MenuItem key={proposal.id} value={proposal.id}>
                          {proposal.name ||
                            proposal.custom_id ||
                            `Proposal ${proposal.id}`}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.jobCosting && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ mt: 0.5, display: "block" }}
                      >
                        {errors.jobCosting}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="Proposal Price"
                    value={financialData.jobCostingProposalPrice}
                    onChange={handleJobCostingPriceChange}
                    fullWidth
                    size="small"
                    variant="outlined"
                    error={!!errors.jobCostingPrice}
                    helperText={errors.jobCostingPrice}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment
                          position="start"
                          sx={{ marginRight: "4px" }}
                        >
                          <span style={{ marginTop: "-3px", fontSize: "14px" }}>
                            $
                          </span>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions
        sx={{
          justifyContent: "flex-end",
          margin: "5px",
          marginLeft: "10px",
        }}
      >
        <Button
          sx={{ marginRight: "10px" }}
          variant="outlined"
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button variant="contained" onClick={handleContinue}>
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FinancialInfoPromptDialog;
